import { useCallback, useMemo } from "react";
import {
  dateKindOf,
  dayStringToLocal,
  isDayString,
  toDayString,
  type DateKind,
  type DateKindSource,
} from "@salestrak/custom-fields";
import {
  DatePicker,
  formatDay,
  type DatePickerMode,
  type DatePickerProps,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import { toDateValue } from "#app/lib/custom-field-utils";

/** The library picker mode per date kind. */
const PICKER_MODE: Record<DateKind, DatePickerMode> = {
  date: "date",
  dateTime: "datetime",
  time: "time",
};

/** Trigger label for a stored day shown by an instant-mode picker: the day
 *  alone, since the value carries no time yet. */
const DAY_ONLY_FORMAT = "MMM d, yyyy";

export type FieldDatePickerProps = Omittable<
  Omit<DatePickerProps, "mode" | "value" | "onValueChange"> & {
    /** The field the value belongs to — its type and `includeTime` config
     *  pick the mode and the stored shape. */
    field: DateKindSource;
    /** The stored value: a "YYYY-MM-DD" day, an ISO instant, a Date, or
     *  empty. */
    value: unknown;
    /** A calendar-day field yields the day string; the instant and time
     *  kinds yield the Date. */
    onValueChange: (next: Date | string | null) => void;
    words?: boolean;
  }
>;

function isLocalMidnight(date: Date): boolean {
  return (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  );
}

/**
 * The `DatePicker` for a date-family custom field. The mode comes from the
 * field (a `date` field with `includeTime` is the instant picker; `time` is
 * the clock), never from a caller's guess at the type.
 *
 * The stored shape crosses here. A calendar day is "YYYY-MM-DD": it is read
 * as local midnight of that day so the calendar highlights the named day,
 * and a pick writes the chosen local day back as a day string. Instants pass
 * through as Dates. A day string reaching an instant-mode picker (the field
 * turned `includeTime` on after the value was set) shows as the day alone
 * and becomes an instant the first time a time is picked for it. A day picked
 * on an empty instant field is stored as a day the same way.
 */
export function FieldDatePicker({
  field,
  value,
  onValueChange,
  words = false,
  ...picker
}: FieldDatePickerProps) {
  const kind = dateKindOf(field) ?? "date";
  const storedDay = isDayString(value);
  const pickerValue = useMemo(
    () => (isDayString(value) ? dayStringToLocal(value) : toDateValue(value)),
    [value],
  );
  // A day picked on an instant field that holds no time yet (empty, or a
  // stored day) is still a day: the picker hands it over as local midnight,
  // and stored as an instant it would read "12:00 AM" — a time nobody
  // picked. It becomes an instant once a time is picked for it.
  const holdsNoTime = pickerValue === null || storedDay;
  const handleValueChange = useCallback(
    (next: Date | null) => {
      const dayOnly =
        next !== null &&
        (kind === "date" || (kind === "dateTime" && holdsNoTime && isLocalMidnight(next)));
      onValueChange(next && dayOnly ? toDayString(next) : next);
    },
    [kind, holdsNoTime, onValueChange],
  );
  return (
    <DatePicker
      {...picker}
      dateFormat={
        storedDay && kind === "dateTime" ? DAY_ONLY_FORMAT : picker.dateFormat
      }
      formatValue={
        words && kind !== "time"
          ? (date, hourCycle) =>
              formatDay(date, { hourCycle, time: kind === "dateTime" && !storedDay })
          : picker.formatValue
      }
      mode={PICKER_MODE[kind]}
      onValueChange={handleValueChange}
      value={pickerValue}
    />
  );
}
